import type { SxProps, Theme } from '@mui/material/styles';

// Shared elevation shadow used across copilot components
const elevation1 =
  '0px 2px 8px -1px rgba(13,27,39,0.03), 0px 1px 5px 0px rgba(13,27,39,0.03), 0px 1px 10px 0px rgba(13,27,39,0.03)';
const shadowSm = '0px 1px 2px 0px rgba(0,0,0,0.05)';

// AiCopilotDrawer (persistent) — width is set dynamically via context
export const drawerPaper: SxProps<Theme> = {
  bgcolor: '#fafbff',
  borderLeft: '1px solid #e8ebf2',
  backgroundImage: 'none',
  overflow: 'hidden',
} as const;

// Resize handle (left edge of drawer)
export const copilotResizeHandle: SxProps<Theme> = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 6,
  cursor: 'ew-resize',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&::after': {
    content: '""',
    width: 2,
    height: 32,
    borderRadius: 1,
    bgcolor: 'transparent',
    transition: 'background-color 0.15s',
  },
  '&:hover::after': {
    bgcolor: '#AFB6C4',
  },
} as const;

// ChatHeader
export const chatHeaderRoot: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  p: '20px',
  height: 72,
  flexShrink: 0,
} as const;

export const chatHeaderTitleGroup: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
} as const;

export const chatHeaderTitle: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: 14,
  lineHeight: '20px',
  color: '#01060d',
  fontFamily: 'Inter, sans-serif',
  overflow: 'hidden',
} as const;

export const chatHeaderCloseIcon: SxProps<Theme> = {
  fontSize: 16,
  color: '#152232',
} as const;

// Applied to the IconButton (not the icon child) so it can color the
// `currentColor` strokes of the raw SVG from `@vy/ui-design-system` via CSS
// cascade. `fontSize` was dropped — it has no effect on raw SVGs.
export const chatHeaderNewChatIcon: SxProps<Theme> = {
  color: '#152232',
} as const;

// ChatMessage
export const chatMessageUserBubble: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  mb: 1.5,
} as const;

export const chatMessageAssistantBubble: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  mb: 1.5,
} as const;

export const chatMessageUserBubbleInner: SxProps<Theme> = {
  maxWidth: '85%',
  px: 1,
  py: 1,
  borderRadius: '10px',
  bgcolor: '#2953FF',
  color: '#fff',
} as const;

export const chatMessageAssistantBubbleInner: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
} as const;

export const chatMessageAssistantBubbleInnerAnimated: SxProps<Theme> = {
  ...chatMessageAssistantBubbleInner,
  animation: 'bubbleFadeIn 0.3s ease-out',
  '@keyframes bubbleFadeIn': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
} as const;

// ThinkingIndicator — animated shimmer text
export const copilotThinkingContainer: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  py: 0.5,
} as const;

export const copilotThinkingDot: SxProps<Theme> = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  bgcolor: '#152232',
  flexShrink: 0,
} as const;

export const copilotThinkingText: SxProps<Theme> = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '20px',
  color: 'transparent',
  backgroundImage: 'linear-gradient(90deg, #152232 0%, rgba(21,34,50,0.3) 50%, #152232 100%)',
  backgroundSize: '200% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: 'thinkingShimmer 2s ease-in-out infinite',
  '@keyframes thinkingShimmer': {
    '0%': { backgroundPosition: '100% 0' },
    '100%': { backgroundPosition: '-100% 0' },
  },
} as const;

export const chatMessageText: SxProps<Theme> = {
  fontSize: 14,
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  fontFamily: 'Inter, sans-serif',
  color: '#152232',
} as const;

export const chatMessageActionsDivider: SxProps<Theme> = {
  width: '100%',
  height: '1px',
  bgcolor: '#e8ebf2',
} as const;

export const chatMessageActionsRow: SxProps<Theme> = {
  display: 'flex',
  marginTop: '16px',
  alignItems: 'center',
  gap: '16px',
} as const;

export const chatMessageActionIcon: SxProps<Theme> = {
  padding: 0,
  color: '#7E8796',
  cursor: 'pointer',
  '&:hover': { color: '#152232' },
} as const;

// ChatInput
export const chatInputRoot: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  px: '20px',
  py: '8px',
  flexShrink: 0,
  background: 'linear-gradient(180deg, rgba(250,251,255,0) 0%, #fafbff 100%)',
} as const;

export const chatInputFieldContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  bgcolor: '#fff',
  border: '1px solid #d6dbe7',
  borderRadius: '12px',
  boxShadow: '0px 0px 0px 2px rgba(0,0,0,0.05)',
  overflow: 'hidden',
  px: '16px',
  py: '12px',
  height: 96,
} as const;

export const chatInputTextarea: SxProps<Theme> = {
  flex: 1,
  resize: 'none',
  border: 'none',
  fontSize: 14,
  lineHeight: '20px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  color: '#020f20',
  bgcolor: 'transparent',
  padding: 0,
  outline: 'none',
  overflow: 'hidden',
  '&::placeholder': { color: '#5f6978', opacity: 1 },
} as const;

export const chatInputActionsRow: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
} as const;

export const chatInputSendButton: SxProps<Theme> = {
  width: 32,
  height: 32,
  bgcolor: '#a4adbe',
  borderRadius: '100px',
  boxShadow: shadowSm,
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 0,
  '&:hover': { bgcolor: '#8f97a8' },
  '&:disabled': { bgcolor: '#a4adbe', color: '#fff', cursor: 'default' },
} as const;

export const chatInputSendButtonEnabled: SxProps<Theme> = {
  ...chatInputSendButton,
  bgcolor: '#2953FF',
  '&:hover': { bgcolor: '#1e42db' },
  '&:disabled': undefined,
} as const;

export const chatInputStopButton: SxProps<Theme> = {
  width: 32,
  height: 32,
  bgcolor: '#a4adbe',
  borderRadius: '100px',
  boxShadow: shadowSm,
  color: '#fff',
  p: 0,
  '&:hover': { bgcolor: '#8f97a8' },
} as const;

// Footer
export const chatFooter: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  alignItems: 'center',
  justifyContent: 'center',
  py: '6px',
  borderRadius: '10px',
} as const;

export const chatFooterPoweredBy: SxProps<Theme> = {
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
} as const;

export const chatFooterPoweredByText: SxProps<Theme> = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: 12,
  lineHeight: '16px',
  color: '#5f6978',
} as const;

export const chatFooterDisclaimer: SxProps<Theme> = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: 12,
  lineHeight: '16px',
  color: '#a4adbe',
  textAlign: 'center',
} as const;

// ChatPanel
export const chatPanelRoot: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
} as const;

export const chatPanelScrollArea: SxProps<Theme> = {
  flex: 1,
  overflowY: 'auto',
  px: 3,
  py: 2,
} as const;

// EmptyState
export const emptyStateRoot: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  flex: 1,
  gap: '32px',
  px: '20px',
  py: '24px',
  minHeight: 0,
} as const;

export const emptyStateIconCircle: SxProps<Theme> = {
  width: 56,
  height: 56,
  borderRadius: '233px',
  bgcolor: '#fff',
  boxShadow: elevation1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

export const emptyStateTextBlock: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
} as const;

export const emptyStateHeading: SxProps<Theme> = {
  fontSize: 24,
  fontWeight: 600,
  lineHeight: '30px',
  color: '#01060d',
  fontFamily: 'Inter, sans-serif',
} as const;

export const emptyStateSubtext: SxProps<Theme> = {
  fontSize: 24,
  fontWeight: 400,
  lineHeight: '30px',
  color: '#01060d',
  fontFamily: 'Inter, sans-serif',
} as const;

export const emptyStateSuggestionList: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
} as const;

export const emptyStateSuggestionButton: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  height: 48,
  px: '16px',
  py: '8px',
  borderRadius: '8px',
  bgcolor: '#fff',
  border: '1px solid #e8ebf2',
  boxShadow: elevation1,
  cursor: 'pointer',
  textTransform: 'none',
  justifyContent: 'flex-start',
  '&:hover': {
    bgcolor: '#f5f6fa',
    borderColor: '#5f6978',
  },
  '&:active': {
    bgcolor: '#f5f6fa',
    borderColor: '#5f6978',
  },
} as const;

export const emptyStateSuggestionText: SxProps<Theme> = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '20px',
  color: '#152232',
  whiteSpace: 'nowrap',
} as const;
